import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BookOpen, Boxes, Code2, Megaphone, FileText, Sparkles, KeyRound,
  ArrowLeft, ShieldCheck, Lock, Store, Plus, Landmark,
  Rocket, Globe, Smartphone, Bot,
} from "lucide-react";
import { api } from "../lib/api";

const SERVICE_ICONS = { BookOpen, Boxes, Code2, Megaphone, FileText, Sparkles };

const GOALS = [
  "تنظيم الواقع التجاري وربطه بأربع مدن سعودية ذكية.",
  "توفير فرص عمل حر للشباب العاطل والمرأة في بيتها.",
  "دعم السائقين والحرفيين والتجار الصغار بواجهة موحدة.",
  "توظيف التطور التكنولوجي والذكاء الاصطناعي لخدمة الوطن.",
  "الشفافية الكاملة في كل عملية عبر رموز AQL و AQ المشفرة.",
  "تحويل المدن السعودية إلى مدن ذكية بمعايير عالمية.",
];

function Hero({ stats }) {
  const items = [
    { v: stats?.requests_completed ?? 0, l: "طلب مكتمل" },
    { v: stats?.requests_registered ?? 0, l: "طلب مسجل" },
    { v: stats?.ads_approved ?? 0, l: "إعلان معتمد" },
  ];
  return (
    <section className="relative overflow-hidden pt-32 pb-16">
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="absolute left-1/2 top-24 h-[420px] w-[760px] -translate-x-1/2 hero-glow blur-3xl" />
      <div className="relative mx-auto flex max-w-3xl flex-col items-center px-5 text-center">
        <span className="fade-up flex h-20 w-20 items-center justify-center rounded-3xl brand-gradient text-2xl font-extrabold text-[#052027] shadow-2xl">
          A4
        </span>
        <h1 className="fade-up mt-6 font-display text-6xl font-extrabold leading-none tracking-tight md:text-7xl" style={{ animationDelay: ".05s" }}>
          <span className="text-gold">AQL4</span>
        </h1>
        <div className="fade-up mt-1 font-display text-3xl font-bold text-cyan md:text-4xl" style={{ animationDelay: ".08s" }}>AQ</div>
        <h2 className="fade-up mt-4 font-display text-2xl font-bold text-white md:text-3xl" style={{ animationDelay: ".1s" }}>
          منصة آقطار الخدمات الرقمية
        </h2>
        <p className="fade-up mt-3 max-w-xl text-muted-foreground" style={{ animationDelay: ".13s" }}>
          وكيل الإعلانات التجارية، والخدمات الذكية، والمدن الذكية، والخدمات الداعمة والتنموية، والعمل الحر — داخل المملكة العربية السعودية.
        </p>

        <div className="fade-up mt-8 grid w-full max-w-lg grid-cols-3 gap-3" style={{ animationDelay: ".16s" }}>
          {items.map((s) => (
            <div key={s.l} className="glass rounded-2xl border border-white/10 py-5">
              <div className="font-display text-3xl font-bold text-cyan">{s.v}</div>
              <div className="mt-1 text-xs text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Goals() {
  return (
    <section className="relative mx-auto max-w-5xl px-5 py-16 lg:px-8">
      <div className="text-center">
        <span className="rounded-full border border-cyan/25 bg-cyan/5 px-3 py-1 text-xs font-medium text-cyan">أهدافنا</span>
        <h2 className="mt-4 font-display text-4xl font-bold text-white">لماذا AQL4؟</h2>
      </div>
      <div className="mt-10 grid gap-4 md:grid-cols-2">
        {GOALS.map((g, i) => (
          <div key={i} className="card-hover flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-cyan/30 bg-cyan/10 font-display text-sm font-bold text-cyan">
              {i + 1}
            </span>
            <p className="text-sm leading-relaxed text-muted-foreground">{g}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function AdsSection({ categories }) {
  return (
    <section className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
      <div className="text-right">
        <span className="rounded-full border border-cyan/25 bg-cyan/5 px-3 py-1 text-xs font-medium text-cyan">قسم مجاني</span>
        <h2 className="mt-4 font-display text-3xl font-bold text-white md:text-4xl">
          ١· الإعلانات التجارية والمدن الذكية والعمل الحر
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          4 تصنيفات × 4 مدن × 13 قسم — الاستخدام والاستعراض مجانيان بالكامل. النشر برسم 50 ريال فقط.
        </p>
      </div>

      <div className="mt-10 grid gap-5 md:grid-cols-2">
        {categories.map((c) => (
          <div key={c.id} className="card-hover overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
            <div className="relative h-44 overflow-hidden">
              <img src={c.image} alt={c.name} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e13] via-transparent to-transparent" />
              <span className="absolute left-3 top-3 rounded-md bg-black/50 px-2 py-1 font-en text-[11px] text-cyan" dir="ltr">n4.aql4.app</span>
              <h3 className="absolute bottom-3 right-4 font-display text-xl font-bold text-white">{c.name}</h3>
            </div>
            <div className="p-5">
              <p className="text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
              <div className="mt-4 flex items-center gap-3">
                <Link to={`/category/${c.id}`}
                  className="btn-cyan inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold">
                  دخول <ArrowLeft className="h-4 w-4" />
                </Link>
                <Link to={`/category/${c.id}?add=1`}
                  className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-white hover:bg-white/5">
                  <Plus className="h-4 w-4" /> إضافة إعلان
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ServicesSection({ services }) {
  return (
    <section className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
      <div className="text-right">
        <span className="rounded-full border border-gold/25 bg-amber-400/5 px-3 py-1 text-xs font-medium text-gold">
          مقفلة برمز باقة · مجاني للاستكشاف
        </span>
        <h2 className="mt-4 font-display text-3xl font-bold text-white md:text-4xl">٢· الخدمات الذكية الداعمة للتجار</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          تُفتح كل خدمة بمفتاح تفعيل يصدر بعد شراء الباقة (رسم رمزي لتشغيل الخدمات فقط).
        </p>
      </div>

      <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {services.map((s) => {
          const Icon = SERVICE_ICONS[s.icon] || Sparkles;
          return (
            <Link key={s.id} to={`/service/${s.id}`}
              className="card-hover group relative rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-md bg-amber-400/10 px-2 py-0.5 text-[11px] font-medium text-gold">
                <KeyRound className="h-3 w-3" /> مفتاح
              </span>
              <Icon className="h-7 w-7 text-cyan" />
              <h3 className="mt-4 font-display text-lg font-bold text-white">{s.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan">
                تشغيل <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
              </span>
            </Link>
          );
        })}
      </div>

      <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-center">
        <p className="text-sm text-muted-foreground">ليس لديك مفتاح بعد؟</p>
        <Link to="/marketplace" className="btn-gold inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold">
          <Store className="h-4 w-4" /> اذهب إلى المتجر
        </Link>
      </div>
    </section>
  );
}

function TrustSection() {
  return (
    <section className="relative mx-auto max-w-6xl px-5 py-16 lg:px-8">
      <div className="rounded-3xl border border-emerald-400/15 bg-gradient-to-br from-emerald-500/8 via-transparent to-cyan-500/8 p-8">
        <h3 className="font-display text-xl font-bold text-white">بيان: منصة غير هادفة للربح التجاري</h3>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          <span className="font-bold text-gold">AQL4</span> منصة سجل حفظ ودعم — <span className="font-semibold text-white">لا نأخذ أي مبالغ من أي عملية شراء أو بيع</span> تتم بين المواطنين والتجار.
          خدماتنا الأساسية مجانية بالكامل ونقدّمها للعاطلين، والمرأة في بيتها، والسائقين، والحرفيين، والتجار الصغار.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          <span className="font-semibold text-white">إخلاء المسؤولية:</span> المنصة إعلانية وداعمة فقط، ولا تتدخل في أي عملية تجارية مباشرة ولا تتحمل مسؤولية أي التزام بين الأطراف.
        </p>
      </div>

      <div className="mt-6 grid gap-5 md:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <div className="flex items-center gap-2 text-cyan"><ShieldCheck className="h-5 w-5" /><span className="font-display font-bold">نظام AQL (العام)</span></div>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            رمز عام مشفّر يُصدر لكل عملية (إعلان، طلب، عقد). فريد لا يتكرر، مرئي للعميل والتاجر، يُستخدم للاستعلام والتتبع.
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <div className="flex items-center gap-2 text-gold"><Lock className="h-5 w-5" /><span className="font-display font-bold">AQ (السري)</span></div>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            رمز HMAC مشفر لا يُخزَّن على الخادم، يُصدر عند الحاجة لإغلاق أو توثيق العملية. لا شراء يُغلق بدون AQ.
          </p>
        </div>
      </div>

      <div className="mt-6 flex items-start gap-3 rounded-2xl border border-gold/20 bg-amber-400/5 p-6">
        <Landmark className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
        <p className="text-sm leading-relaxed text-amber-100/90">
          <span className="font-semibold text-gold">حول الأسعار:</span> كل الخدمات الذكية تُقدّم بسعر رمزي جدًا يذهب لتشغيل الوكلاء والخدمات فقط. لا أرباح تجارية — النشر في الأقسام التجارية برسم رمزي <span className="font-bold">50 ريال للإعلان الواحد</span>، وباقي الاستكشاف مجاني بالكامل.
        </p>
      </div>
    </section>
  );
}

function BuildCTA() {
  return (
    <section className="relative mx-auto max-w-6xl px-5 py-16 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-cyan/20 bg-gradient-to-br from-cyan-500/10 via-transparent to-amber-400/10 p-8 md:p-12">
        <div className="absolute -left-10 -top-10 h-40 w-40 rounded-full bg-cyan/20 blur-3xl" />
        <div className="relative grid items-center gap-8 md:grid-cols-[1.4fr_1fr]">
          <div>
            <span className="rounded-full border border-cyan/25 bg-cyan/5 px-3 py-1 text-xs font-medium text-cyan">قسم البناء الكامل</span>
            <h2 className="mt-4 font-display text-3xl font-bold text-white md:text-4xl">ابنِ مواقع وتطبيقات ووكلاء ذكاء</h2>
            <p className="mt-3 text-muted-foreground">
              استوديو بناء كامل بالذكاء الاصطناعي — صِف فكرتك بالعربية وشاهدها تُبنى مع معاينة حية، تمامًا كطريقة البناء الاحترافية.
            </p>
            <Link to="/build" className="btn-cyan mt-6 inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold">
              <Rocket className="h-5 w-5" /> ادخل الاستوديو
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[{ Icon: Globe, l: "مواقع" }, { Icon: Smartphone, l: "تطبيقات" }, { Icon: Bot, l: "وكلاء" }].map(({ Icon, l }) => (
              <div key={l} className="flex flex-col items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] py-6">
                <Icon className="h-7 w-7 text-cyan" />
                <span className="text-sm font-medium text-white">{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [pages, setPages] = useState([]);

  useEffect(() => {
    api.stats().then(setStats).catch(() => {});
    api.categories().then((d) => setCategories(d.categories || [])).catch(() => {});
    api.services().then((d) => setServices(d.services || [])).catch(() => {});
    api.pages().then(setPages).catch(() => {});
  }, []);

  return (
    <main>
      <Hero stats={stats} />
      <Goals />
      <AdsSection categories={categories} />
      <ServicesSection services={services} />
      {pages.length > 0 && (
        <section className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <div className="text-right">
            <span className="rounded-full border border-emerald-400/25 bg-emerald-400/5 px-3 py-1 text-xs font-medium text-emerald">أقسام إضافية</span>
            <h2 className="mt-4 font-display text-3xl font-bold text-white md:text-4xl">أقسام المنصة</h2>
          </div>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {pages.map((p) => (
              <Link key={p.id} to={`/p/${p.slug}`} className="card-hover rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                <h3 className="font-display text-lg font-bold text-white">{p.title}</h3>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan">دخول <ArrowLeft className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </section>
      )}
      <BuildCTA />
      <TrustSection />
    </main>
  );
}
